import React, { Component, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import HelperClass from "../../config/helperClass";
import {
  createStripePaymentIntent,
  createStripeCheckoutSession,
  getStripePaymentStatus,
} from "../../config/client_api_calls";

const auth = new HelperClass();

/*
 * Pay-an-invoice sheet.
 *
 * Behaviour follows new-obs-mockups/client/invoices.html (#mPay); the
 * presentation reuses the client portal's own pay-modal vocabulary that already
 * ships in Pages/Client/invoices/styles.css (.pay-modal-card, .pay-summary-strip,
 * .pay-fields-grid, .pay-breakdown, .pay-modal-footer, .pay-history-table), so it
 * matches the Record Payment sheet the admin portal shows for the same invoice.
 *
 * Card, Link and wallets are confirmed inside the portal with Stripe's Payment
 * Element in deferred-intent mode: the element collects details first, the
 * PaymentIntent is created on Pay, then confirmed. Bank debits (ACH / SEPA /
 * Bacs) go through Stripe Checkout, and the invoice stays open until Stripe
 * says the money arrived.
 *
 * Props: show, invoice, config (from /stripe/client-config), decimals,
 * onClose(), onPaid(result), initialReturn ({kind, session_id})
 */

const stripePromises = {};
function stripeFor(pk) {
  if (!pk) return null;
  if (!stripePromises[pk]) stripePromises[pk] = loadStripe(pk);
  return stripePromises[pk];
}

function money(n, decimals) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "$0.00";
  const fixed = v.toFixed(decimals == null ? 2 : decimals);
  const [i, d] = fixed.split(".");
  return "$" + i.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + (d !== undefined ? "." + d : "");
}

function balanceOf(inv) {
  if (inv && inv.balance_due != null && Number.isFinite(Number(inv.balance_due))) return Math.max(0, Number(inv.balance_due));
  return Math.max(
    0,
    (Number(inv && inv.grand_total) || 0) - (Number(inv && inv.credit_applied) || 0) - (Number(inv && inv.amount_paid) || 0)
  );
}

/* What the client can choose between, in the order they are offered. `need` is
   the admin switch that has to be on; `instant` says whether paying settles the
   invoice there and then — a bank debit has not cleared yet and must not. */
const PAY_METHODS = [
  { k: "saved", need: "card", saved: true, instant: true, icon: "ni-cc-alt" },
  { k: "card", need: "card", instant: true, icon: "ni-cc", t: "A different card", s: "Visa, Mastercard or Amex." },
  { k: "link", need: "link", instant: true, icon: "ni-link-alt", label: "Link", t: "Link", s: "Pay with the card you have saved with Stripe." },
  { k: "wallet", need: "wallet", instant: true, icon: "ni-mobile", label: "Apple Pay", t: "Apple Pay / Google Pay", s: "Confirm with Face ID or a fingerprint." },
  { k: "ach", need: "ach", icon: "ni-building", label: "Bank debit", t: "Bank debit (ACH)", s: "Cheaper for larger invoices. Three to five working days." },
  { k: "sepa", need: "sepa", icon: "ni-building", label: "SEPA debit", t: "SEPA direct debit", s: "From a euro account. Up to five working days." },
  { k: "bacs", need: "bacs", icon: "ni-building", label: "Bacs debit", t: "Bacs direct debit", s: "From a UK account. Up to five working days." },
];

/** Inner form: needs the Elements context, so it is a function component. */
function PayForm({ method, saveCard, canSave, onSaveChange, wallets, onSubmitRef, error }) {
  const stripe = useStripe();
  const elements = useElements();
  const [ready, setReady] = useState(false);

  // Expose submit() to the class component through a ref-like callback.
  onSubmitRef(async () => {
    if (!stripe || !elements) return { error: { message: "The payment form is still loading." } };
    const { error: submitError } = await elements.submit();
    if (submitError) return { error: submitError };
    return { stripe, elements };
  });

  const options = {
    layout: "tabs",
    wallets: { applePay: wallets ? "auto" : "never", googlePay: wallets ? "auto" : "never" },
    paymentMethodOrder: method === "link" ? ["link", "card"] : ["card", "link"],
  };

  return (
    <div className="pay-element">
      {!ready && (
        <div className="pay-element-loading">
          <span className="spinner-border spinner-border-sm" style={{ marginRight: 8 }} />
          Loading secure payment form…
        </div>
      )}
      <PaymentElement options={options} onReady={() => setReady(true)} />
      {canSave && (
        <label className="pay-credit-box pay-save-row">
          <input type="checkbox" className="pay-credit-check" checked={saveCard} onChange={(e) => onSaveChange(e.target.checked)} />
          <span>
            <span className="pay-credit-label">Keep this card for future invoices</span>
            <span className="pay-credit-desc">So the next one is a single click, and auto-charge can use it.</span>
          </span>
        </label>
      )}
      {error && <div className="pay-error-box">{error}</div>}
    </div>
  );
}

class StripePayModal extends Component {
  constructor(props) {
    super(props);
    this.submitFn = null;
    this.pollTimer = null;
    this.state = this.fresh(props);
  }

  fresh(props) {
    const inv = props.invoice;
    const due = inv ? balanceOf(inv) : 0;
    const saved = props.config && props.config.saved_method;
    return {
      amount: due ? String(Number(due.toFixed(2))) : "",
      amountBad: false,
      method: saved ? "saved" : "card",
      saveCard: true,
      busy: false,
      error: "",
      step: "form", // form | done
      done: null,
    };
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.show && this.props.show) {
      this.setState(this.fresh(this.props), () => {
        if (this.props.initialReturn) this.handleReturn(this.props.initialReturn);
      });
    }
    if (prevProps.show && !this.props.show) {
      clearTimeout(this.pollTimer);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.pollTimer);
  }

  // ── helpers ────────────────────────────────────────────────────────────────

  cfg() {
    return this.props.config || { methods: {}, where: {}, mode: "test" };
  }
  offered() {
    const cfg = this.cfg();
    return PAY_METHODS.filter((m) => {
      if (!cfg.methods || !cfg.methods[m.need]) return false;
      if (m.saved && !cfg.saved_method) return false;
      return true;
    });
  }
  current() {
    return PAY_METHODS.filter((m) => m.k === this.state.method)[0] || null;
  }
  amountNum() {
    const v = parseFloat(String(this.state.amount).replace(/[^0-9.]/g, ""));
    return Number.isFinite(v) ? Math.round(v * 100) / 100 : 0;
  }
  due() {
    return this.props.invoice ? balanceOf(this.props.invoice) : 0;
  }
  returnUrl() {
    const base = window.location.origin + window.location.pathname;
    return `${base}?invoice=${encodeURIComponent(this.props.invoice.invoice_no)}`;
  }

  close = () => {
    clearTimeout(this.pollTimer);
    if (this.props.onClose) this.props.onClose();
  };

  pick = (k) => this.setState({ method: k, error: "" });

  amtChanged = (e) => this.setState({ amount: e.target.value, amountBad: false, error: "" });
  payFull = () => this.setState({ amount: String(Number(this.due().toFixed(2))), amountBad: false });

  validateAmount() {
    const amt = this.amountNum();
    const due = this.due();
    if (!(amt >= 0.5) || amt > due + 0.005) {
      this.setState({ amountBad: true });
      return null;
    }
    return amt;
  }

  // ── paying ─────────────────────────────────────────────────────────────────

  go = async () => {
    const amt = this.validateAmount();
    if (amt == null) return;
    const m = this.current();
    if (!m) {
      this.setState({ error: "No payment method is available." });
      return;
    }
    if (!m.instant) return this.startCheckout(m, amt);
    if (m.saved) return this.chargeSaved(amt);
    return this.confirmElement(amt);
  };

  async createIntent(amt, useSaved) {
    const res = await createStripePaymentIntent(
      auth.getClientAccount(),
      auth.getClientToken(),
      this.props.invoice.invoice_no,
      amt,
      this.state.saveCard && this.cfg().where && this.cfg().where.autopay !== false,
      useSaved
    );
    const payload = res && res.data;
    if (payload && (payload.status === 403 || payload.errors === "jwt expired" || payload.errors === "authentication missing")) {
      auth.clientLogout();
      throw new Error("Your session has expired.");
    }
    if (!payload || payload.status !== 200 || !payload.data) {
      throw new Error((payload && payload.message) || "Could not start the payment.");
    }
    return payload.data;
  }

  confirmElement = async (amt) => {
    if (!this.submitFn) return;
    this.setState({ busy: true, error: "" });
    try {
      const pre = await this.submitFn();
      if (pre.error) throw new Error(pre.error.message || "Check the card details.");
      const intent = await this.createIntent(amt, false);
      const { stripe, elements } = pre;
      const result = await stripe.confirmPayment({
        elements,
        clientSecret: intent.client_secret,
        confirmParams: { return_url: this.returnUrl() + "&stripe=success" },
        redirect: "if_required",
      });
      if (result.error) throw new Error(result.error.message || "The payment was not completed.");
      this.finish(result.paymentIntent, amt, intent);
    } catch (e) {
      this.setState({ busy: false, error: e.message || "The payment was not completed." });
    }
  };

  chargeSaved = async (amt) => {
    this.setState({ busy: true, error: "" });
    try {
      const intent = await this.createIntent(amt, true);
      if (intent.declined) throw new Error(intent.declined);
      let pi = { id: intent.payment_intent_id, status: intent.intent_status };
      if (intent.requires_action && intent.client_secret) {
        const stripe = await stripeFor(intent.publishable_key || this.cfg().publishable_key);
        const r = await stripe.confirmCardPayment(intent.client_secret);
        if (r.error) throw new Error(r.error.message || "The card was not accepted.");
        pi = r.paymentIntent;
      }
      if (pi.status !== "succeeded" && pi.status !== "processing") throw new Error("The card was not accepted. Try another way to pay.");
      this.finish(pi, amt, intent);
    } catch (e) {
      this.setState({ busy: false, error: e.message || "The payment was not completed." });
    }
  };

  startCheckout = async (m, amt) => {
    this.setState({ busy: true, error: "" });
    try {
      const res = await createStripeCheckoutSession(
        auth.getClientAccount(),
        auth.getClientToken(),
        this.props.invoice.invoice_no,
        amt,
        m.k,
        this.returnUrl(),
        this.state.saveCard
      );
      const payload = res && res.data;
      if (!payload || payload.status !== 200 || !payload.data || !payload.data.url) {
        throw new Error((payload && payload.message) || "Could not start the bank payment.");
      }
      window.location.href = payload.data.url;
    } catch (e) {
      this.setState({ busy: false, error: e.message });
    }
  };

  /** A confirmed intent: show the receipt and wait for the ledger to catch up. */
  finish(pi, amt, intent) {
    const m = this.current();
    const cfg = this.cfg();
    const shown =
      m && m.saved && cfg.saved_method
        ? `${cfg.saved_method.brand || "Card"} •••• ${cfg.saved_method.last4 || ""}`
        : m && m.k === "link"
        ? "Link"
        : m && m.k === "wallet"
        ? "Apple Pay / Google Pay"
        : "Card";
    const processing = pi.status === "processing";
    const stillDue = Math.max(0, this.due() - amt);
    this.setState({
      busy: false,
      step: "done",
      done: {
        kind: processing ? "processing" : "paid",
        amount: amt,
        ref: (pi && pi.id) || (intent && intent.payment_intent_id) || "",
        method: shown,
        stillDue,
        recorded: false,
      },
    });
    this.pollLedger(0);
  }

  /** Bank-debit return: the invoice stays open until the money arrives. */
  handleReturn(ret) {
    if (ret.kind === "cancel") {
      this.setState({ error: "The bank payment was cancelled. Nothing was charged." });
      return;
    }
    this.setState({
      step: "done",
      done: { kind: "pending", amount: null, ref: ret.session_id || "", method: "Bank debit", stillDue: null, recorded: false },
    });
    this.pollLedger(0);
  }

  pollLedger = async (attempt) => {
    if (!this.props.invoice) return;
    try {
      const res = await getStripePaymentStatus(auth.getClientAccount(), auth.getClientToken(), this.props.invoice.invoice_no, true);
      const payload = res && res.data;
      const d = payload && payload.status === 200 && payload.data;
      if (d) {
        const done = this.state.done || {};
        const attemptRow = d.latest_attempt || {};
        const recorded =
          d.stripe_payment_status === "succeeded" ||
          (done.kind === "paid" && Number(d.balance_due) <= Math.max(0, done.stillDue || 0) + 0.005);
        const pendingBank = done.kind === "pending" && attemptRow.status && attemptRow.status !== "succeeded";
        this.setState({
          done: Object.assign({}, done, {
            recorded: recorded || (done.kind === "pending" && d.stripe_payment_status === "succeeded"),
            stillDue: d.balance_due,
            amount: done.amount != null ? done.amount : attemptRow.amount,
            ref: done.ref || attemptRow.payment_intent_id || attemptRow.checkout_session_id || "",
            method: done.method || attemptRow.payment_method_summary || done.method,
          }),
        });
        if ((recorded && done.kind !== "pending") || (done.kind === "pending" && d.stripe_payment_status === "succeeded")) {
          if (this.props.onPaid) this.props.onPaid(d);
          return;
        }
        if (pendingBank && attempt >= 3) {
          if (this.props.onPaid) this.props.onPaid(d);
          return;
        }
      }
    } catch (e) {
      /* keep polling */
    }
    if (attempt < 10) this.pollTimer = setTimeout(() => this.pollLedger(attempt + 1), attempt < 3 ? 1500 : 3000);
    else if (this.props.onPaid) this.props.onPaid(null);
  };

  // ── render ─────────────────────────────────────────────────────────────────

  renderMethods() {
    const cfg = this.cfg();
    const opts = this.offered();
    const cur = this.state.method;
    if (!opts.length) return <div className="pay-field-hint">No payment method is available right now.</div>;
    return (
      <div className="pay-method-list">
        {opts.map((o) => {
          const sm = cfg.saved_method || {};
          const brand = sm.brand ? sm.brand.charAt(0).toUpperCase() + sm.brand.slice(1) : "Card";
          const t = o.saved ? `${brand} •••• ${sm.last4 || ""}` : o.t;
          const sub = o.saved
            ? `Expires ${sm.exp_month ? String(sm.exp_month).padStart(2, "0") : "--"} / ${
                sm.exp_year ? String(sm.exp_year).slice(-2) : "--"
              }. Charged straight away.`
            : o.s;
          return (
            <label className={"pay-method" + (cur === o.k ? " active" : "")} key={o.k}>
              <input type="radio" name="stripePayMethod" value={o.k} checked={cur === o.k} onChange={() => this.pick(o.k)} />
              <em className={"icon ni " + o.icon}></em>
              <span className="pay-method-txt">
                <span className="pay-method-t">{t}</span>
                <span className="pay-method-s">{sub}</span>
              </span>
            </label>
          );
        })}
      </div>
    );
  }

  renderForm() {
    const inv = this.props.invoice;
    const cfg = this.cfg();
    const st = this.state;
    const m = this.current();
    const d = this.props.decimals;
    const amt = this.amountNum();
    const due = this.due();
    const instantElement = m && m.instant && !m.saved;
    const stripePromise = stripeFor(cfg.publishable_key);
    const canSave = !!cfg.auto_create_customer && cfg.where && cfg.where.autopay !== false;
    const grandTotal = Number(inv.grand_total) || 0;
    const alreadyPaid = Number(inv.amount_paid) || 0;
    const elementsOptions = {
      mode: "payment",
      amount: Math.max(50, Math.round(amt * 100)),
      currency: String(inv.currency || cfg.currency || "USD").toLowerCase(),
      paymentMethodTypes: ["card"].concat(cfg.methods && cfg.methods.link ? ["link"] : []),
      appearance: { theme: "stripe", variables: { colorPrimary: "#1A7A4A", borderRadius: "8px", fontSizeBase: "13px" } },
    };
    if (canSave && st.saveCard) elementsOptions.setupFutureUsage = "off_session";

    return (
      <>
        <div className="pay-modal-body">
          {/* Invoice summary strip — same three figures the admin sheet shows */}
          <div className="pay-summary-strip">
            <div className="pay-summary-col">
              <div className="pay-summary-label">INVOICE TOTAL</div>
              <div className="pay-summary-val">{money(grandTotal, d)}</div>
            </div>
            <div className="pay-summary-col">
              <div className="pay-summary-label">ALREADY PAID</div>
              <div className="pay-summary-val pay-summary-green">{money(alreadyPaid, d)}</div>
            </div>
            <div className="pay-summary-col">
              <div className="pay-summary-label">BALANCE DUE</div>
              <div className="pay-summary-val pay-summary-orange">{money(due, d)}</div>
            </div>
            {inv.billing_period && (
              <div style={{ gridColumn: "1/-1", marginTop: 8, fontSize: 12, color: "#6B6762" }}>
                Billing period: <strong>{inv.billing_period}</strong>
              </div>
            )}
          </div>

          {cfg.mode === "test" && (
            <div className="pay-testbar">
              <em className="icon ni ni-info-fill"></em>
              <span>
                <strong>Test mode.</strong> Nothing is charged. Use card 4242 4242 4242 4242, any future expiry and any CVC.
              </span>
            </div>
          )}

          {/* Amount */}
          <div className="pay-fields-grid pay-fields-1">
            <div className="pay-field-group">
              <label className="pay-field-label">Amount to Pay (USD) *</label>
              <div className="pay-amount-row">
                <input
                  className="pay-field-input"
                  inputMode="decimal"
                  value={st.amount}
                  onChange={this.amtChanged}
                  disabled={st.busy}
                  style={st.amountBad ? { borderColor: "#dc2626" } : undefined}
                />
                <button type="button" className="pay-cancel-btn pay-full-btn" onClick={this.payFull} disabled={st.busy}>
                  Full balance
                </button>
              </div>
              {st.amountBad ? (
                <div className="pay-field-error">Enter an amount up to the outstanding balance of {money(due, d)}.</div>
              ) : (
                <div className="pay-field-hint">
                  {amt > 0 && amt < due ? `Leaves ${money(due - amt, d)} outstanding on this invoice.` : "Pays this invoice in full."}
                </div>
              )}
            </div>
          </div>

          {/* Method */}
          <div className="pay-section-label">How you would like to pay</div>
          {this.renderMethods()}

          {instantElement && stripePromise && (
            <Elements
              stripe={stripePromise}
              options={elementsOptions}
              key={elementsOptions.currency + ":" + (elementsOptions.setupFutureUsage || "")}
            >
              <PayForm
                method={st.method}
                saveCard={st.saveCard}
                canSave={canSave && st.method !== "link"}
                onSaveChange={(v) => this.setState({ saveCard: v })}
                wallets={!!(cfg.methods && cfg.methods.wallet)}
                onSubmitRef={(fn) => (this.submitFn = fn)}
                error={st.error}
              />
            </Elements>
          )}
          {instantElement && !stripePromise && <div className="pay-error-box">The payment form could not be loaded.</div>}

          {m && !m.instant && (
            <div className="pay-note-box">
              You will be sent to your bank to approve a direct debit. Bank payments take three to five working days to clear, and
              the invoice stays open until they do.
            </div>
          )}
          {!instantElement && st.error && <div className="pay-error-box">{st.error}</div>}

          <div className="pay-secure-note">
            <em className="icon ni ni-shield-check"></em>
            <span>
              Card details go straight to Stripe. {cfg.business_name || "This portal"} never sees or stores your card number.
            </span>
          </div>
        </div>

        <div className="pay-modal-footer">
          <button type="button" className="pay-cancel-btn" onClick={this.close} disabled={st.busy}>
            Cancel
          </button>
          <button type="button" className="pay-confirm-btn" onClick={this.go} disabled={st.busy || !m}>
            {st.busy ? (
              <>
                <span className="spinner-border spinner-border-sm" style={{ marginRight: 6, width: 14, height: 14, borderWidth: 2 }} />
                Working…
              </>
            ) : !m ? (
              "Pay"
            ) : m.instant ? (
              `✓ Pay ${money(amt, d)}`
            ) : (
              "Set up the debit"
            )}
          </button>
        </div>
      </>
    );
  }

  renderDone() {
    const inv = this.props.invoice;
    const cfg = this.cfg();
    const done = this.state.done || {};
    const d = this.props.decimals;
    const pending = done.kind === "pending" || done.kind === "processing";
    const heading = pending ? (done.kind === "pending" ? "Debit set up" : "Payment processing") : `Paid ${money(done.amount, d)}`;
    const sub = pending
      ? `It will leave your account in a few working days. ${inv.invoice_no} stays open until it clears.`
      : done.stillDue != null && Number(done.stillDue) > 0.005
      ? `${money(done.stillDue, d)} still outstanding on ${inv.invoice_no}.`
      : `${inv.invoice_no} is settled in full.`;
    return (
      <>
        <div className="pay-modal-body">
          <div className="pay-done">
            <div className={"pay-done-tick" + (pending ? " pending" : "")}>
              <em className={"icon ni " + (pending ? "ni-clock" : "ni-check")}></em>
            </div>
            <div className="pay-done-h">{heading}</div>
            <div className="pay-done-s">{sub}</div>
            {!done.recorded && !pending && (
              <div className="pay-done-s" style={{ marginTop: 8 }}>
                <span className="spinner-border spinner-border-sm" style={{ marginRight: 6, width: 12, height: 12, borderWidth: 2 }} />
                Updating your invoice…
              </div>
            )}
          </div>

          <div className="pay-breakdown">
            <div className="pay-breakdown-title">PAYMENT DETAILS</div>
            <div className="pay-breakdown-row">
              <span>Invoice</span>
              <span>{inv.invoice_no}</span>
            </div>
            {done.amount != null && (
              <div className="pay-breakdown-row">
                <span>Amount</span>
                <span className="pay-breakdown-green">{money(done.amount, d)}</span>
              </div>
            )}
            <div className="pay-breakdown-row">
              <span>Method</span>
              <span>{done.method || "—"}</span>
            </div>
            {done.ref ? (
              <div className="pay-breakdown-row pay-ref-row">
                <span>Reference</span>
                <span className="pay-ref">{done.ref}</span>
              </div>
            ) : null}
            {cfg.mode === "test" && (
              <div className="pay-breakdown-row">
                <span>Mode</span>
                <span>Test — no money moved</span>
              </div>
            )}
          </div>
        </div>

        <div className="pay-modal-footer">
          <button type="button" className="pay-confirm-btn pay-done-btn" onClick={this.close}>
            Done
          </button>
        </div>
      </>
    );
  }

  render() {
    const { show, invoice } = this.props;
    if (!show || !invoice) return null;
    const d = this.props.decimals;
    return (
      <div
        className="modal fade show"
        style={{ display: "block", background: "rgba(0,0,0,0.45)", zIndex: 1060 }}
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "760px" }}>
          <div className="pay-modal-card">
            <div className="pay-modal-header">
              <div>
                <div className="pay-modal-title">Pay {invoice.invoice_no}</div>
                <div className="pay-modal-subtitle">
                  {invoice.invoice_title || invoice.orderno || "Invoice"} · Balance Due: {money(this.due(), d)}
                </div>
              </div>
              <button className="pay-modal-close" onClick={this.close} disabled={this.state.busy}>
                ×
              </button>
            </div>
            {this.state.step === "done" ? this.renderDone() : this.renderForm()}
          </div>
        </div>
      </div>
    );
  }
}

export default StripePayModal;
